import { CREATE_ITEM, CREATE_LIST, EDIT_ITEM, EDIT_LIST, DELETE_ITEM, DELETE_LIST, LIKE_ITEM } from "constants/index"

type ACTION_TYPE =
  "CREATE_ITEM"
  | "EDIT_ITEM"
  | "DELETE_ITEM"
  | "CREATE_LIST"
  | "EDIT_LIST"
  | "DELETE_LIST"
  | "LIKE_ITEM"

export function getUpdatedStates(type: ACTION_TYPE,
                                 userProfileLists: WishList[] | null,
                                 listInfo: any,
                                 itemInfo: any,
                                 userWishItems: WishItem[] = []
) {
  switch (type) {
    case CREATE_LIST:
      return getUpdateStatesForCreatingList(userProfileLists, listInfo)
    case EDIT_LIST:
      return getUpdateStatesForEditingList(userProfileLists, listInfo)
    case DELETE_LIST:
      return getUpdateStatesForDeletingList(userProfileLists, listInfo.id)
    case CREATE_ITEM:
      return getUpdateStatesForCreatingItem(userProfileLists, listInfo, itemInfo, userWishItems)
    case EDIT_ITEM:
      return getUpdateStatesForEditingItem(userProfileLists, listInfo, itemInfo, userWishItems)
    case DELETE_ITEM:
      return getUpdateStatesForDeletingItem(userProfileLists, listInfo, itemInfo, userWishItems)
    case LIKE_ITEM:
      return getUpdateStatesForLikingItem(userProfileLists, listInfo, itemInfo, userWishItems)
    default:
      throw new Error(`Unsupported action type for getUpdatedStates method. Action type: ${type}`)
  }
}

function getUpdateStatesForCreatingList(userProfileLists: WishList[] | null, list: WishList) {
  const createdListInfo = createListInfoObject(list)
  const updatedUserProfileLists = addListLocally(userProfileLists, createdListInfo)
  return {
    listInfo: createdListInfo,
    userProfileLists: updatedUserProfileLists,
    userWishLists: getWishListsArrayFromUserProfileList(updatedUserProfileLists),
    itemInfo: null,
    userWishItems: null
  }
}

function createListInfoObject(listInfo: WishList) {
  return {
    ...listInfo,
    id: Number(listInfo.id),
    listItems: null,
    itemCount: 0,
    createdAt: new Date(),
    updatedAt: new Date()
  }
}

function addListLocally(lists: WishList[] | null, createdList: WishList) {
  if (!createdList.id) throw new Error(`Invalid list. Passed list has no id. List: ${createdList}`)
  const listsCpy = structuredClone(lists)

  if (listsCpy) {
    listsCpy.push(createdList)
  }

  return listsCpy
}

function getUpdateStatesForEditingList(userProfileLists: WishList[] | null, list: WishList) {
  const updatedUserProfileLists = editListLocally(userProfileLists, list)
  return {
    listInfo: list,
    userProfileLists: updatedUserProfileLists,
    userWishLists: getWishListsArrayFromUserProfileList(updatedUserProfileLists),
    itemInfo: null,
    userWishItems: null
  }
}

function editListLocally(lists: WishList[] | null, editedList: WishList) {
  if (!editedList.id) throw new Error(`Invalid list. Passed list has no id. List: ${editedList}`)
  const listsCpy = structuredClone(lists)
  let updatedListsCpy = null

  if (listsCpy) {
    updatedListsCpy = listsCpy.map(list => {
      if (Number(list.id) === Number(editedList.id)) {
        list.name = editedList.name
        list.privacy = editedList.privacy
      }
      return list
    })
  }

  return updatedListsCpy
}

function getUpdateStatesForDeletingList(userProfileLists: WishList[] | null, id: number) {
  const updatedUserProfileLists = deleteListLocally(userProfileLists, id)
  return {
    listInfo: null,
    userProfileLists: updatedUserProfileLists,
    userWishLists: getWishListsArrayFromUserProfileList(updatedUserProfileLists),
    itemInfo: null,
    userWishItems: null
  }
}

function deleteListLocally(lists: WishList[] | null, id: number) {
  if (!id) throw new Error("id is not passed!")
  const listsCpy = structuredClone(lists)
  return listsCpy ? listsCpy.filter(l => l.id !== Number(id)) : null
}

function getUpdateStatesForCreatingItem(userProfileLists: WishList[] | null, list: WishList, item: SingleWishItem, userWishItems: WishItem[]) {
  const createdItemInfo = createItemInfoObject(userProfileLists, item)
  const updatedUserProfileLists = addCreatedItemToListsLocally(userProfileLists, createdItemInfo)
  return {
    listInfo: addCreatedItemToListLocally(list, createdItemInfo),
    userProfileLists: updatedUserProfileLists,
    userWishLists: getWishListsArrayFromUserProfileList(updatedUserProfileLists),
    itemInfo: createdItemInfo,
    userWishItems: [...userWishItems, createdItemInfo]
  }
}

function createItemInfoObject(lists: WishList[] | null, item: any) {
  const { wishlistIds, ...itemInfo } = item
  const filteredLists = lists?.filter(list => wishlistIds.includes(list.id)).map(list => ({
    ...list,
    itemCount: 0,
    listItems: null
  }))
  return {
    ...itemInfo,
    id: item.id,
    image: item.image.size === 0 ? "" : item.image,
    saveCount: 0,
    shareCount: 0,
    likeCount: 0,
    commentCount: 0,
    isLiked: false,
    wishlists: filteredLists || [],
    createdAt: new Date(),
    updatedAt: new Date()
  }
}

function addCreatedItemToListLocally(list: WishList | null, item: SingleWishItem) {
  const listCpy = structuredClone(list)
  if (listCpy) {
    if (!listCpy.listItems) {
      listCpy.listItems = []
    }
    listCpy.listItems.push(item)
    listCpy.itemCount = listCpy?.listItems?.length || 0
  }
  return listCpy
}

function addCreatedItemToListsLocally(lists: WishList[] | null, item: SingleWishItem) {
  const { wishlists, ...itemInfo } = item
  const wishListIds = wishlists.map(list => list.id)
  if (!wishListIds.length) return lists
  const listsCpy = structuredClone(lists)
  if (listsCpy) {
    listsCpy.forEach(list => {
      if (wishListIds.includes(list.id)) {
        if (!list?.listItems) {
          list.listItems = [itemInfo]
        } else {
          list.listItems.push(itemInfo)
        }
        list.itemCount = list?.listItems?.length || 0
      }
    })
  }
  return listsCpy
}

function getUpdateStatesForEditingItem(userProfileLists: WishList[] | null, list: WishList, item: SingleWishItem, userWishItems: WishItem[]) {
  const itemCpy = structuredClone(item)
  if (itemCpy.image instanceof File) {
    if (itemCpy.image.size === 0) {
      itemCpy.image = ""
    }
  }
  const updatedUserProfileLists = applyEditedItemToListsLocally(userProfileLists, itemCpy)
  return {
    listInfo: applyEditedItemToListLocally(list, itemCpy),
    userProfileLists: updatedUserProfileLists,
    userWishLists: getWishListsArrayFromUserProfileList(updatedUserProfileLists),
    itemInfo: itemCpy,
    userWishItems: applyEditedItemToWishItemsLocally(userWishItems, itemCpy)
  }
}

function applyEditedItemToListLocally(list: WishList, item: SingleWishItem) {
  const { wishlists, ...itemInfo } = item
  const wishListIds = wishlists.map(list => list.id)
  const listCpy = structuredClone(list)
  if (listCpy && listCpy.listItems) {
    if (wishListIds.includes(list.id)) {
      listCpy.listItems = listCpy.listItems.map(listItem => {
        if (Number(listItem.id) === Number(item.id)) {
          listItem = item
        }
        return listItem
      })
    } else {
      listCpy.listItems = listCpy.listItems.filter(listItem => Number(listItem.id) !== Number(item.id))
      if (listCpy.listItems.length === 0) {
        listCpy.listItems = null
      }
    }
    listCpy.itemCount = listCpy.listItems?.length || 0
  }
  return listCpy
}

function applyEditedItemToListsLocally(lists: WishList[] | null, item: SingleWishItem) {
  const { wishlists, ...itemInfo } = item
  const wishListIds = wishlists.map(list => list.id)
  const listsCpy = structuredClone(lists)
  if (listsCpy) {
    listsCpy.forEach(list => {
      if (wishListIds.includes(list.id)) {
        if (!list?.listItems) {
          list.listItems = [itemInfo]
        } else {
          const itemWasInList = list.listItems.find(listItem => Number(listItem.id) === Number(item.id))
          if (itemWasInList) {
            list.listItems = list.listItems.map(listItem => {
              if (Number(listItem.id) === Number(item.id)) {
                listItem = item
              }
              return listItem
            })
          } else {
            list.listItems.push(item)
          }
        }
      } else {
        if (list.listItems) {
          list.listItems = list.listItems.filter(listItem => Number(listItem.id) !== Number(item.id))
          if (list.listItems.length === 0) {
            list.listItems = null
          }
        }
      }
      list.itemCount = list?.listItems?.length || 0
    })
  }
  return listsCpy
}

function applyEditedItemToWishItemsLocally(items: WishItem[], item: SingleWishItem) {
  return items.map(currentItem => {
    if (Number(currentItem.id) === Number(item.id)) {
      return { ...currentItem, ...item }
    } else {
      return currentItem
    }
  })
}

function getUpdateStatesForDeletingItem(userProfileLists: WishList[] | null, list: WishList, item: SingleWishItem, userWishItems: WishItem[]) {
  const updatedUserProfileLists = applyDeletedItemToListsLocally(userProfileLists, item)
  return {
    listInfo: applyDeletedItemToListLocally(list, item),
    userProfileLists: updatedUserProfileLists,
    userWishLists: getWishListsArrayFromUserProfileList(updatedUserProfileLists),
    itemInfo: null,
    userWishItems: applyDeletedItemToWishItemsLocally(userWishItems, item)
  }
}

function applyDeletedItemToListLocally(list: WishList, item: SingleWishItem) {
  const listCpy = structuredClone(list)
  if (listCpy) {
    if (listCpy.listItems) {
      listCpy.listItems = listCpy.listItems.filter(listItem => Number(listItem.id) !== Number(item.id))
      if (listCpy.listItems?.length === 0) {
        listCpy.listItems = null
      }
    }
    listCpy.itemCount = listCpy.listItems?.length || 0
  }
  return listCpy
}

function applyDeletedItemToListsLocally(lists: WishList[] | null, item: SingleWishItem) {
  const { wishlists, ...itemInfo } = item
  const listsCpy = structuredClone(lists)
  if (listsCpy) {
    listsCpy.forEach(list => {
      if (list.listItems) {
        list.listItems = list.listItems.filter(listItem => Number(listItem.id) !== Number(item.id))
        if (list.listItems.length === 0) {
          list.listItems = null
        }
      }
      list.itemCount = list?.listItems?.length || 0
    })
  }
  return listsCpy
}

function applyDeletedItemToWishItemsLocally(items: WishItem[], item: SingleWishItem) {
  return items.filter(currentItem => Number(currentItem.id) !== Number(item.id))
}

function getWishListsArrayFromUserProfileList(lists: WishList[] | null) {
  if (!lists) return lists
  const listsCpy = structuredClone(lists)
  return listsCpy.map(list => {
    list.listItems = null
    return list
  })
}

function getUpdateStatesForLikingItem(userProfileLists: WishList[] | null, list: WishList, item: SingleWishItem, userWishItems: WishItem[]) {
  const updatedItem = {
    ...item,
    isLiked: !item.isLiked,
    likeCount: item.isLiked ? item.likeCount - 1 : item.likeCount + 1
  }
  const updatedUserProfileLists = applyEditedItemToListsLocally(userProfileLists, updatedItem)
  return {
    listInfo: applyLikedItemToListLocally(list, updatedItem),
    userProfileLists: updatedUserProfileLists,
    userWishLists: getWishListsArrayFromUserProfileList(updatedUserProfileLists),
    itemInfo: updatedItem,
    userWishItems: applyEditedItemToWishItemsLocally(userWishItems, updatedItem)
  }
}

function applyLikedItemToListLocally(list: WishList, item: SingleWishItem) {
  const listCpy = structuredClone(list)
  if (!listCpy || !listCpy.listItems) return list
  listCpy.listItems = listCpy.listItems.map(listItem => {
    if (Number(listItem.id) === Number(item.id)) {
      listItem.isLiked = item.isLiked
      listItem.likeCount = item.likeCount
    }
    return listItem
  })
  return listCpy
}
